import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import OpinionForm from '../../components/OpinionForm';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { AUTH_CONFIG } from '../../config';

const EditOpinion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opinion, setOpinion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  
  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Use the shared AUTH_CONFIG helper instead of direct fetch
        const isAuth = await AUTH_CONFIG.isAuthenticated();
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthChecking(false);
      }
    };
    
    checkAuth();
  }, []);

  // Fetch the opinion data
  useEffect(() => {
    const fetchOpinion = async () => {
      try {
        setIsLoading(true);
        
        const response = await fetch(`/api/opinions/${id}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Opinion not found');
          }
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setOpinion(data);
        setError(null);
      } catch (err) {
        setError(`Failed to load opinion: ${err.message}`);
        setOpinion(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isAuthenticated && id) {
      fetchOpinion();
    }
  }, [id, isAuthenticated]);

  // Handle form submission
  const handleSubmit = async (opinionData) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      const response = await fetch('/api/opinions/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(opinionData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to update opinion');
      }
      
      // Redirect to the admin list after successful update
      navigate('/admin/opinions');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle opinion deletion
  const handleDelete = async (opinionId) => {
    try {
      setIsSubmitting(true);
      
      const response = await fetch(`/api/opinions/${opinionId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to delete opinion');
      }
      
      // Redirect to the admin list after successful deletion
      navigate('/admin/opinions');
    } catch (err) {
      setError(`Failed to delete opinion: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/opinions');
  };

  // Content to render when authenticated and data is loaded
  const authContent = (
    <div className="container mx-auto px-4 max-w-5xl">
      <Helmet>
        <title>Edit Opinion | Admin</title>
      </Helmet>
      
      <div className="mb-6">
        <Link to="/admin/opinions" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to opinion management
        </Link>
      </div>
      
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">
            {isLoading ? 'Loading Opinion...' : opinion ? opinion.title : 'Opinion Not Found'}
          </h1>
          {opinion && (
            <p className="mt-1 text-sm text-gray-500">
              By {opinion.author} • Status: {opinion.status.charAt(0).toUpperCase() + opinion.status.slice(1)}
            </p>
          )}
        </div>
        
        <div className="px-6 py-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-gray-600">Loading opinion data...</p>
            </div>
          ) : error && !opinion ? (
            <div className="py-8 bg-red-50 rounded-lg text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Opinion</h2>
              <p className="text-red-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/admin/opinions')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              >
                Back to Opinion List
              </button>
            </div>
          ) : error ? (
            <div className="p-4 mb-6 bg-red-50 border-l-4 border-red-500 text-red-700">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 mr-2" />
                <span>{error}</span>
              </div>
            </div>
          ) : opinion && (
            <OpinionForm
              opinion={opinion}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={isSubmitting}
              submitButtonText="Update Opinion"
              showDelete={true}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Loading spinner while checking auth
  const loadingContent = (
    <div className="text-center py-8">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      <p className="mt-2 text-gray-600">Checking authentication...</p>
    </div>
  );

  // Not authenticated message
  const unauthContent = (
    <div className="py-8 bg-red-50 rounded-lg text-center">
      <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
      <h2 className="text-xl font-bold text-red-800 mb-2">Authentication Required</h2>
      <p className="text-red-600 mb-6">
        You must be logged in as an administrator to access this area.
      </p>
      <Link
        to="/admin"
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
      >
        Go to Admin Login
      </Link>
    </div>
  );

  return (
    <AdminLayout>
      {authChecking ? loadingContent : !isAuthenticated ? unauthContent : authContent}
    </AdminLayout>
  );
};

export default EditOpinion;